import { useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  trackReadingSessionEnd,
  trackReadingSessionStart,
} from "../events/reading";
import { readerNavigationContext } from "../../hookstate/readerNavigationContextStore";
import { usePageLayout } from "../../screens/reader/hooks/usePageLayout";
import { useReadingDirection } from "../../screens/reader/hooks/useReadingDirection";
import { useReleaseData } from "../../screens/reader/hooks/hookstate/useReleaseData";
import { useReleasePagesData } from "../../screens/reader/hooks/hookstate/useReleasePagesData";
import { getNavigationAnalyticsAttribution } from "../params/navigationContextAttribution";
import { mapReadingSourceFromNavigation } from "../params/source";
import { createClientUuid } from "../createClientUuid";
import { useAccountData } from "../../hooks/useAccountData";

type ReadingSessionState = {
  sessionUuid: string;
  startedAtMs: number;
  startPage: number;
  currentPage: number;
  maxPageReached: number;
  pagesRead: Set<number>;
  releaseId: number;
  comicId: number;
  artistId: number;
  lang: string;
  totalPages: number;
  viewerMode: string;
  accessMode: string;
};

function toEventPage(pageNumber: number): number {
  return Math.max(pageNumber - 1, 0);
}

export function useReadingSessionTracking(): void {
  const accountData = useAccountData();
  const { releaseData } = useReleaseData();
  const { releasePagesData } = useReleasePagesData();
  const { pageLayout } = usePageLayout();
  const { comicIsVertical } = useReadingDirection();
  const { pageOrder } = useParams();

  const sessionRef = useRef<ReadingSessionState | null>(null);
  const startSessionRef = useRef<() => void>(() => {});
  const endSessionRef = useRef<() => void>(() => {});

  const currentPage = Math.max(parseInt(pageOrder ?? "1", 10), 1);
  const viewerMode = comicIsVertical ? "vertical" : (pageLayout ?? "single");
  const userIsGold = !!accountData?.user?.gold;
  const userIsFreeTrial = !!accountData?.subscription_summary?.is_free_trial;
  const accessMode = releaseData?.is_free
    ? "free"
    : releaseData?.release_user_purchase
      ? "purchase"
      : userIsGold
        ? (userIsFreeTrial ? "trial" : "subscription")
        : "preview";

  const endSession = useCallback(() => {
    const session = sessionRef.current;
    if (!session) {
      return;
    }

    const durationMs = Math.max(0, Date.now() - session.startedAtMs);
    trackReadingSessionEnd({
      readingSessionUuid: session.sessionUuid,
      releaseId: session.releaseId,
      comicId: session.comicId,
      artistId: session.artistId,
      lang: session.lang,
      startPage: toEventPage(session.startPage),
      endPage: toEventPage(session.currentPage),
      maxPageReached: toEventPage(session.maxPageReached),
      totalPages: session.totalPages,
      pagesReadInSession: session.pagesRead.size,
      sessionDurationMs: durationMs,
      viewerMode: session.viewerMode,
      accessMode: session.accessMode,
    });

    sessionRef.current = null;
  }, []);

  const startSession = useCallback(() => {
    if (!releaseData?.id || !releaseData.comic?.id || sessionRef.current) {
      return;
    }
    const navigationContext = readerNavigationContext.get({ noproxy: true });
    const source = mapReadingSourceFromNavigation(navigationContext);
    const { impressionId } =
      getNavigationAnalyticsAttribution(navigationContext);

    const totalPages = Math.max(
      releasePagesData?.length ?? releaseData.page_count ?? 1,
      1,
    );
    const sessionUuid = createClientUuid();
    const nextSession: ReadingSessionState = {
      sessionUuid,
      startedAtMs: Date.now(),
      startPage: currentPage,
      currentPage,
      maxPageReached: currentPage,
      pagesRead: new Set([currentPage]),
      releaseId: releaseData.id,
      comicId: releaseData.comic.id,
      artistId: releaseData.comic.artist?.id ?? 0,
      lang: releaseData.lang_id,
      totalPages,
      viewerMode,
      accessMode,
    };

    sessionRef.current = nextSession;

    trackReadingSessionStart({
      readingSessionUuid: sessionUuid,
      releaseId: nextSession.releaseId,
      comicId: nextSession.comicId,
      artistId: nextSession.artistId,
      lang: nextSession.lang,
      startPage: toEventPage(nextSession.startPage),
      totalPages,
      isFirstRead: !releaseData.user_read_status?.last_page_read,
      isResuming: currentPage > 1,
      viewerMode,
      accessMode,
      source,
      impressionId,
    });
  }, [
    accessMode,
    currentPage,
    releaseData,
    releasePagesData?.length,
    viewerMode,
  ]);

  startSessionRef.current = startSession;
  endSessionRef.current = endSession;

  useEffect(() => {
    if (!releaseData?.id || !releaseData.comic?.id) {
      return;
    }

    if (sessionRef.current?.releaseId === releaseData.id) {
      return;
    }

    endSession();
    startSession();
  }, [endSession, releaseData?.id, releaseData?.comic?.id, startSession]);

  useEffect(() => {
    const session = sessionRef.current;
    if (!session) {
      return;
    }

    session.currentPage = currentPage;
    session.maxPageReached = Math.max(session.maxPageReached, currentPage);
    session.pagesRead.add(currentPage);
  }, [currentPage]);

  useEffect(() => {
    const onPageHide = () => {
      endSessionRef.current();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        endSessionRef.current();
        return;
      }

      if (document.visibilityState === "visible") {
        startSessionRef.current();
      }
    };

    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      endSessionRef.current();
    };
  }, []);
}
